/**
 * Zentraler Update-Status der App.
 *
 * Zwei Quellen melden eine neue Version:
 *  - der Service Worker (vite-plugin-pwa, registerType 'prompt' → onNeedRefresh in main.tsx)
 *  - der Versions-Check gegen /version.json (useVersionCheck, Fallback ohne SW)
 *
 * Statt die Seite sofort neu zu laden (Formulareingaben gingen verloren), wird ein
 * Banner angezeigt (UpdateBanner). Erst „Jetzt laden“ führt applyUpdate() aus.
 */
import { createSignal } from 'solid-js';

type UpdateSource = 'sw' | 'version';

const [updateAvailable, setUpdateAvailable] = createSignal<UpdateSource | null>(null);
const [applying, setApplying] = createSignal(false);

let swUpdateFn: ((reloadPage?: boolean) => Promise<void>) | null = null;

/** Von main.tsx: die updateSW-Funktion aus registerSW() hinterlegen. */
export function setServiceWorkerUpdater(fn: (reloadPage?: boolean) => Promise<void>): void {
  swUpdateFn = fn;
}

/** Eine neue Version ist verfügbar – Banner einblenden. Die SW-Quelle hat Vorrang. */
export function markUpdateAvailable(source: UpdateSource): void {
  if (source === 'sw' || updateAvailable() === null) setUpdateAvailable(source);
}

export { updateAvailable, applying as updateApplying };

/**
 * Update übernehmen. Wartender SW → SKIP_WAITING (vite-pwa lädt bei 'controlling' neu).
 * Ohne wartenden SW (reiner Versionsunterschied) → Caches leeren, SW abmelden, neu laden.
 */
export async function applyUpdate(): Promise<void> {
  if (applying()) return;
  setApplying(true);

  // Falls der Reload über den SW aus irgendeinem Grund nicht kommt: nach 4 s hart neu laden.
  const fallback = window.setTimeout(() => window.location.reload(), 4000);

  try {
    let waiting: ServiceWorker | null = null;
    if ('serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        waiting = reg?.waiting ?? null;
      } catch { /* ignorieren */ }
    }

    if (waiting && swUpdateFn) {
      await swUpdateFn(true);
      return; // Reload kommt über 'controlling'-Event bzw. den Fallback-Timer
    }

    // Kein wartender SW: Caches leeren, Registrierungen entfernen, neu laden
    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch { /* trotzdem neu laden */ }
    }
    window.clearTimeout(fallback);
    window.location.reload();
  } catch {
    window.clearTimeout(fallback);
    window.location.reload();
  }
}
